import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { dataService } from "../services/dataService";
import { useError } from "./ErrorContext";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/apiService";

const NotesContext = createContext();

export function NotesProvider({ children }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showError } = useError();
  const [folders, setFolders] = useState(["All Notes"]);
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const navigate = useNavigate();

  // Load notes and folders when user is authenticated
  useEffect(() => {
    const loadData = async () => {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Load notes
        const fetchedNotes = await dataService.getNotes();
        setNotes(fetchedNotes);

        // Load folders from API
        const fetchedFolders = await apiService.get("/api/folders");
        setFolders(["All Notes", ...fetchedFolders]);
      } catch (error) {
        console.error("Error loading data:", error);
        if (
          error.message.includes("Token expired") ||
          error.message.includes("Invalid token")
        ) {
          localStorage.removeItem("auth_token");
          navigate("/login");
        } else {
          showError("Failed to load data");
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showError, navigate]);

  const createNote = useCallback(
    async (noteData) => {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        showError("Please login to create notes");
        return null;
      }

      try {
        console.log("Creating note with data:", noteData); // Debug log

        // Ensure required fields are present
        const processedData = {
          title: noteData.title || "Untitled",
          content: noteData.content || "",
          folder: noteData.folder || "",
          keyPoints: noteData.keyPoints || [],
          userId: JSON.parse(localStorage.getItem("user")).userId,
        };

        const newNote = await dataService.createNote(processedData);
        console.log("New note created:", newNote); // Debug log

        // Immediately update the notes array with the new note
        setNotes((prev) => [...prev, newNote]);

        // Update folders if needed
        if (processedData.folder && !folders.includes(processedData.folder)) {
          setFolders((prev) => [...prev, processedData.folder]);
        }

        return newNote;
      } catch (error) {
        console.error("Error creating note:", error);
        showError("Failed to create note");
        return null;
      }
    },
    [folders, showError]
  );

  const updateNote = async (noteId, updatedNote) => {
    try {
      console.log("NotesContext updateNote called with:", {
        noteId,
        updatedNote,
        currentNotes: notes,
      });

      const updatedNoteFromServer = await dataService.updateNote(
        noteId,
        updatedNote
      );

      console.log("Server response:", updatedNoteFromServer);

      // Update local state
      setNotes((prevNotes) => {
        const newNotes = prevNotes.map((note) =>
          note._id === noteId ? updatedNoteFromServer : note
        );
        console.log("Updated notes state:", newNotes);
        return newNotes;
      });

      return updatedNoteFromServer;
    } catch (error) {
      console.error("Error updating note:", error);
      throw error;
    }
  };

  const deleteNote = useCallback(
    async (id) => {
      try {
        await dataService.deleteNote(id);
        setNotes((prev) => prev.filter((note) => note._id !== id));

        // Update folders if needed
        const remainingFolders = [
          ...new Set(
            notes.filter((note) => note._id !== id).map((note) => note.folder)
          ),
        ];
        setFolders([
          "Default",
          ...remainingFolders.filter((f) => f !== "Default"),
        ]);
      } catch (error) {
        console.error("Error deleting note:", error);
        showError("Failed to delete note");
      }
    },
    [notes, showError]
  );

  const addFolder = useCallback(
    async (folderName) => {
      try {
        await apiService.post("/api/folders", { name: folderName });
        setFolders((prev) => [...prev, folderName]);
        return true;
      } catch (error) {
        showError(error.message);
        return false;
      }
    },
    [showError]
  );

  const deleteFolder = useCallback(
    async (folderName) => {
      try {
        // First update all notes in this folder to have empty folder (which shows as "All Notes")
        const notesInFolder = notes.filter(
          (note) => note.folder === folderName
        );
        await Promise.all(
          notesInFolder.map((note) =>
            updateNote(note._id, { ...note, folder: "" })
          )
        );

        // Then delete the folder from the database
        await apiService.delete(`/api/folders/${folderName}`);

        // Update local state
        setFolders((prev) => prev.filter((f) => f !== folderName));
        setNotes((prev) =>
          prev.map((note) =>
            note.folder === folderName ? { ...note, folder: "" } : note
          )
        );

        return true;
      } catch (error) {
        showError(error.message);
        return false;
      }
    },
    [notes, updateNote, showError]
  );

  const updateFolder = useCallback(
    async (oldName, newName) => {
      try {
        // Don't allow renaming Default folder
        if (oldName === "Default") {
          showError("Cannot rename Default folder");
          return false;
        }

        // Don't allow duplicate folder names
        if (folders.includes(newName)) {
          showError("Folder name already exists");
          return false;
        }

        // Update all notes in the old folder
        const notesInFolder = notes.filter((note) => note.folder === oldName);
        await Promise.all(
          notesInFolder.map((note) =>
            updateNote(note._id, { ...note, folder: newName })
          )
        );

        // Update folders list
        setFolders((prev) => prev.map((f) => (f === oldName ? newName : f)));

        return true;
      } catch (error) {
        console.error("Error updating folder:", error);
        showError("Failed to rename folder");
        return false;
      }
    },
    [folders, notes, updateNote, showError]
  );

  const value = {
    notes,
    loading,
    createNote,
    updateNote,
    deleteNote,
    folders,
    addFolder,
    deleteFolder,
    updateFolder,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    updateNotes: setNotes,
  };

  return (
    <NotesContext.Provider value={value}>{children}</NotesContext.Provider>
  );
}

export function useNotes() {
  const context = useContext(NotesContext);
  if (!context) {
    throw new Error("useNotes must be used within a NotesProvider");
  }
  return context;
}
